/* eslint-disable no-unused-vars */
import "./MasterLoginTeamsList.scss"

import {
  FooterVariant,
  Loader,
  MenuItem,
  NavigationListItem,
  PromptVariant,
} from "@skylead/component-library"
import { OverlayScrollbars } from "overlayscrollbars"
import React, { useEffect } from "react"
import InfiniteScroll from "react-infinite-scroller"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import {
  changeTeamName,
  clearForm,
  clearFormErrors,
  closeModal,
  deleteTeam,
  hideInfoModal,
  leaveTeam,
  setFormErrors,
  showModal,
  showPrompt,
  toggleCanManageTeamMembers,
  updateFormField,
} from "../../../redux/actions"
import { store } from "../../../redux/store"
import { useCustomMenu, useGlobalTranslation } from "../../../utils/hook"
import { getUserType, USER_TYPES } from "../../../utils/team-management/team-management-utils"
import { validateNewTeamName, validateNoEmojis } from "../../../utils/validation/validation-utils"
import { hasWhitelabelPermission, WHITELABEL_PERMISSIONS } from "../../../utils/whitelabel-utils"
import LangText from "../../atoms/LangText"
import { ModalVariant } from "../../atoms/Modal"
import InputGroup from "../InputGroup"

const MasterLoginTeamsList = ({
  userTeams = [],
  userData,
  selectedTeam,
  selectTeam,
  userTeamsSumCount,
  loadingTeams,
  loadingSeats,
  loadMore,
  hasMore,
  showTransferSeatsModal,
  isTeamsTabActive,
}) => {
  const { tt } = useGlobalTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [customMenu, setCustomMenu] = useCustomMenu("custom-menu")

  useEffect(() => {
    OverlayScrollbars(document.querySelector(".navigation-list-item-menu"), {
      scrollbars: {
        autoHide: "move",
        autoHideDelay: 800,
      },
    })
  }, [])

  const userType = getUserType(userData.userTypeId)
  const isPlatformAdmin = [USER_TYPES.PLATFORM_CO_OWNER, USER_TYPES.SUPER_ADMIN].includes(userType)
  const isCoWorker = userType === USER_TYPES.CO_WORKER
  const isTeamMember = userType === USER_TYPES.TEAM_MEMBER
  const hasWhitelabelPermissionForTeamManagement = hasWhitelabelPermission(
    WHITELABEL_PERMISSIONS.TEAM_MANAGEMENT,
  )

  const handleSubmitUserNameChange = async team => {
    const { teamName } = store.getState().forms.formData

    dispatch(clearFormErrors())
    const allErrors = validateNewTeamName({ teamName })
    const emojiErrors = validateNoEmojis({ id: "teamName", value: teamName })

    if (allErrors || emojiErrors) {
      dispatch(setFormErrors(allErrors || emojiErrors))
    } else {
      const success = await dispatch(changeTeamName(tt, team.teamId, undefined, navigate))
      if (success) {
        dispatch(hideInfoModal())
      }
    }
  }

  const showChangeCompanyNameModal = async team => {
    dispatch(updateFormField("teamName", team.teamName))
    dispatch(clearForm())
    dispatch(
      showModal(
        ModalVariant.SMALL,
        tt("rename-team"),
        <InputGroup
          field="teamName"
          label={tt("team-name")}
          type="text"
          placeholder={tt("team-name")}
          inputGroupClassName="input-on-surface"
        />,
        FooterVariant.TRANSACTIONAL,
        { label: tt("rename"), onClick: () => handleSubmitUserNameChange(team) },
        {
          label: tt("cancel"),
          onClick: () => {
            clearFormErrors()
            dispatch(closeModal())
          },
        },
      ),
    )
  }

  const handleDeleteTeam = async team => {
    const teamDeleted = await dispatch(deleteTeam(tt, team.teamId, navigate))
    if (teamDeleted) {
      dispatch(closeModal())
    }
  }

  const deleteCompanyConfirmation = team => {
    if (team?.accounts?.length > 0) {
      dispatch(
        showPrompt(
          PromptVariant.WARNING,
          tt("warning"),
          tt("navigation.delete.company.description"),
          undefined,
          tt("close"),
          undefined,
          undefined,
          undefined,
          FooterVariant.TRANSACTIONAL_DESTRUCTIVE,
          false,
        ),
      )
    } else {
      dispatch(
        showPrompt(
          PromptVariant.WARNING,
          tt("warning"),
          tt("navigation.delete.team.description"),
          () => handleDeleteTeam(team),
          tt("delete-team"),
        ),
      )
    }
  }

  const handleLeaveCompany = async teamId => {
    await dispatch(leaveTeam(tt, teamId, navigate))
  }

  const onLeaveTeamClick = team => {
    dispatch(
      showPrompt(
        PromptVariant.WARNING,
        tt("warning"),
        tt("navigation.leave.team.description"),
        () => handleLeaveCompany(team.teamId),
        tt("leave-team"),
      ),
    )
  }

  const renderEmptyState = () => {
    if (userTeams && !userTeams.length && !!userTeamsSumCount) {
      return (
        <span className="no-teams-found">
          <LangText langKey="no-teams-found" />
        </span>
      )
    }
    return null
  }

  const renderTeamMenu = (e, team) => {
    const canViewTeamSettings =
      isPlatformAdmin || (isCoWorker && team.canManageTeamMembers) || team.isTeamOwner
    const canViewAddSeats =
      [USER_TYPES.PLATFORM_CO_OWNER].includes(userType) || (isCoWorker && team.canManagePayment)

    setCustomMenu(
      e,
      <div className="team-menu">
        {canViewTeamSettings && (
          <MenuItem
            icon="pencil-20"
            label={tt("rename-team")}
            onClickContainer={() => {
              showChangeCompanyNameModal(team)
            }}
          />
        )}
        {canViewTeamSettings && canViewAddSeats && (
          <MenuItem
            icon="coins-20"
            label={tt("transfer-seat-credits")}
            onClickContainer={() => {
              showTransferSeatsModal(team)
            }}
          />
        )}
        {hasWhitelabelPermissionForTeamManagement &&
          (isCoWorker || isTeamMember) &&
          !team.isTeamOwner && (
            <MenuItem
              icon="walk-20"
              label={tt("leave-team")}
              onClickContainer={() => {
                onLeaveTeamClick(team)
              }}
            />
          )}
        {canViewTeamSettings && (
          <div>
            <MenuItem
              icon="trash-20"
              label={tt("delete-team")}
              onClickContainer={() => {
                deleteCompanyConfirmation(team)
              }}
              customClass="delete-menu-item"
            />
          </div>
        )}
      </div>,
    )
  }

  return (
    <div className="navigation-list-item-menu">
      {customMenu}
      <InfiniteScroll
        useWindow={false}
        initialLoad={false}
        pageStart={0}
        loadMore={loadMore}
        hasMore={hasMore}
      >
        <>
          {userTeams?.length
            ? userTeams?.map(team => (
                <NavigationListItem
                  key={team.teamId}
                  tt={tt}
                  team={team}
                  isSelected={selectedTeam?.teamId === team.teamId && isTeamsTabActive}
                  isDisabled={loadingSeats}
                  userTypeId={userData.userTypeId}
                  onSelect={selectTeam}
                  onClickMenu={e => {
                    renderTeamMenu(e, team)
                  }}
                  useDispatch={useDispatch}
                  toggleCanManageTeamMembers={toggleCanManageTeamMembers}
                  getUserType={getUserType}
                  USER_TYPES={USER_TYPES}
                  hasWhitelabelPermission={hasWhitelabelPermission}
                  WHITELABEL_PERMISSIONS={WHITELABEL_PERMISSIONS}
                />
              ))
            : renderEmptyState()}
          {loadingTeams && <Loader fullWidth />}
        </>
      </InfiniteScroll>
    </div>
  )
}

// MasterLoginTeamsList.propTypes = {
//   hasMore: PropTypes.bool.isRequired,
//   loadMore: PropTypes.func.isRequired,
//   loadingSeats: PropTypes.bool.isRequired,
//   loadingTeams: PropTypes.bool.isRequired,
//   selectTeam: PropTypes.func.isRequired,
//   selectedTeam: PropTypes.shape({
//     teamId: PropTypes.number,
//   }).isRequired,
//   userData: PropTypes.shape({
//     id: PropTypes.number,
//     userTypeId: PropTypes.number,
//   }).isRequired,
//   showTransferSeatsModal: PropTypes.func.isRequired,
//   userTeams: PropTypes.instanceOf(Array),
//   userTeamsSumCount: PropTypes.number,
//   isTeamsTabActive: PropTypes.bool.isRequired,
// }

export default MasterLoginTeamsList
